/**
 * Изучаемые языки ученика на клиенте: активный язык (сохраняется между
 * запусками) и последний список с сервера.
 *
 * Активный язык уходит заголовком `X-Learning-Language` с каждым запросом
 * (`api/client.ts`): карта, карман, уроки и чат приходят для него. Смена
 * языка перемонтирует экран (`App.tsx`), и он загружает данные заново.
 */

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { DEFAULT_TARGET_LANGUAGE, LANGUAGE_CODES, type LanguageCode, type UserLanguageView } from '@aptile/shared';

interface LearningState {
  readonly activeLanguage: LanguageCode;
  /** Список с сервера; пустой, пока он не загружен. */
  readonly languages: readonly UserLanguageView[];
  readonly setLanguages: (active: LanguageCode, languages: readonly UserLanguageView[]) => void;
}

export const useLearningStore = create<LearningState>()(
  persist(
    (set) => ({
      activeLanguage: DEFAULT_TARGET_LANGUAGE,
      languages: [],
      setLanguages: (activeLanguage, languages) => set({ activeLanguage, languages }),
    }),
    {
      name: 'aptile-learning',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ activeLanguage: state.activeLanguage }),
      merge: (persisted, current) => {
        const stored = (persisted as { readonly activeLanguage?: unknown } | undefined)?.activeLanguage;
        const activeLanguage = LANGUAGE_CODES.find((code) => code === stored) ?? current.activeLanguage;
        return { ...current, activeLanguage };
      },
    },
  ),
);
